'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useContext, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import Button from '@/components/Button';
import { TCard } from '@/containers/EnterpriseAccount/components/HeroNewOffer/components/Cards';
import { AmplitudeContext } from '@/contexts/amplitude';
import { BannerProps } from '@/types/cms';

import HeroModal from './HeroModal';

interface HeroProps
  extends Omit<
    BannerProps,
    'id' | 'referId' | 'machineImage' | 'iconsDescriptions'
  > {
  id: string;
}
export const resourceCards: TCard[] = [
  {
    firstText: 'De 1,22% por',
    mainText: '0,79%',
    lastText: 'no Débito',

    lineThrough: true,
    fontMainText: true,
  },
  {
    firstText: 'De 3,59% por',
    mainText: '2,89%',
    lastText: 'no Crédito 1x',

    lineThrough: true,
    fontMainText: true,
  },
];

const Hero: React.FC<HeroProps> = ({
  bannerImages,
  titleDescriptionTag,
  buttons,
}) => {
  const { analytics } = useContext(AmplitudeContext);
  const [ref, inView] = useInView({
    triggerOnce: true,
  });

  const [showModal, setShowModal] = useState(false);
  const closeModal = () => {
    setShowModal(false);
  };

  useEffect(() => {
    if (inView) {
      analytics?.track({
        event_type: 'section viewed',
        event_properties: {
          name: 'section viewed',
          description:
            'Evento disparado quando o usuário visualiza uma sessão especifica da pagina',
          section_reference: titleDescriptionTag.title,
        },
      });
    }
  }, [analytics, inView, titleDescriptionTag.title]);

  function trackCTA(reference: string) {
    analytics?.track({
      event_type: 'cta stone',
      event_properties: {
        name: 'cta stone',
        description: 'Evento disparado no clique no cta do hero',
        section_reference: titleDescriptionTag.title,
        cta_reference: reference,
      },
    });
  }

  return (
    <>
      <div
        className="flex justify-center items-center bg-display-0"
        id="Context_heroHomePage"
        ref={ref}
      >
        <div className="flex relative flex-col md:flex-row justify-end md:justify-center items-center w-full md:h-[616px] lg:h-[656px]">
          <div className="hidden lg:flex w-full h-[656px]">
            <Image
              src={bannerImages?.web?.path}
              alt={bannerImages?.web?.alternativeText}
              width={1920}
              height={656}
              quality={80}
              priority
              className="object-cover"
              layout="responsive"
            />
          </div>
          <div className="hidden md:flex lg:hidden w-full h-[616px]">
            <Image
              src={bannerImages?.tablet?.path}
              alt={bannerImages?.tablet?.alternativeText}
              width={1920}
              height={616}
              quality={80}
              priority
              className="object-cover"
            />
          </div>
          <div className="flex md:hidden relative bottom-24 order-1 w-full h-full">
            <Image
              src={bannerImages?.mobile?.path}
              alt={bannerImages?.mobile?.alternativeText}
              width={1057}
              height={285}
              quality={80}
              priority
              className="object-cover"
            />
          </div>
          <div className="flex md:absolute left-0 z-10 xl:justify-end xl:items-center p-24 xl:p-40 md:py-40 w-full md:w-1/2 bg-stone-900 md:rounded-none md:rounded-r-base xl:rounded-r-6xl rounded-b-2xl">
            <div className="flex flex-col gap-24 xl:items-end w-full xl:min-w-[600px]">
              <div className="flex flex-col gap-16 justify-center xl:justify-start items-center md:items-start xl:items-start w-full xl:max-w-[601px]">
                <h1 className="md:block h-full font-display text-5xl xl:text-7xl font-black leading-[44px] xl:leading-[56px] text-center xl:text-left text-display-0">
                  As <span className="text-stone-300">melhores taxas</span> pro
                  seu negócio botar pra girar
                </h1>

                <div className="flex flex-col lg:w-full xl:w-auto">
                  <div className="flex flex-wrap gap-8 xl:gap-y-16 xl:w-[471px] xl:max-w-[471px]">
                    <p className="w-full font-sharon text-base md:text-lg md:leading-[26px] text-center xl:text-left text-display-0">
                      Com taxas a partir de
                    </p>
                    <div className="grid grid-cols-2 gap-16 w-full xl:w-auto">
                      {resourceCards.map((item, index) => (
                        <div
                          key={index}
                          className="flex flex-col gap-4 justify-center items-center p-16 xl:w-[170px] bg-[rgba(0,92,65,0.5)] rounded-base xl:rounded-md border-2 border-stone-600"
                        >
                          <h1 className="font-display text-5xl md:text-5xl xl:text-7xl font-black md:leading-[44px] xl:leading-10 text-stone-300">
                            {item.mainText}
                          </h1>
                          <p className="font-sharon text-base font-semibold md:font-bold leading-4 text-display-0">
                            {item.lastText}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="mt-8 xl:mt-24 text-base text-center xl:text-left text-display-0">
                    Confira{' '}
                    <span
                      onClick={() => {
                        setShowModal(true);
                        analytics?.track({
                          event_type: 'cta stone',
                          event_properties: {
                            name: 'cta stone',
                            description:
                              'Evento disparado quando o usuário clicar nos ctas da LP',
                            cta_reference: 'Confira mais detalhes',
                          },
                        });
                      }}
                      className="decoration-slice text-base text-stone-300 underline cursor-pointer"
                    >
                      mais detalhes da oferta
                    </span>
                  </p>
                  <div className="flex xl:grid flex-col xl:flex-row grid-cols-2 gap-16 xl:gap-24 mt-16 xl:mt-24 w-full">
                    {buttons?.map(({ color, label, link }, index) => (
                      <Button
                        key={index}
                        href={link.href}
                        color={color}
                        className="w-full md:w-auto"
                        onClick={() => trackCTA(label)}
                        aria-label={label}
                      >
                        {label}
                      </Button>
                    ))}
                    <Link
                      href="tel:3004 9681"
                      aria-label="Atendimento em 5 segundos no número 3004 9681"
                      onClick={() => trackCTA('3004 9681')}
                      className="flex flex-col flex-wrap flex-1 md:gap-0 items-center xl:items-start md:pt-0 max-w-5xl cursor-pointer"
                    >
                      <p className="max-w-5xl text-center md:text-left text-stone-300 paragraph-14">
                        Atendimento em 5 segundos
                      </p>
                      <span
                        className={`text-2xl text-center md:text-left font-bold whitespace-nowrap paragraph-24 text-display-0`}
                      >
                        3004 9681
                      </span>
                    </Link>
                  </div>
                  <HeroModal showModal={showModal} closeModal={closeModal} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Hero;
